import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { soundManager } from "../lib/sound";

export interface ActiveBurst {
  id: string;
  emoji: string;
  x: number;
  y: number;
  label?: string;
  targetName?: string;
}

export const EmojiBurstOverlay: React.FC = () => {
  const [activeBursts, setActiveBursts] = useState<ActiveBurst[]>([]);

  useEffect(() => {
    const handleSpawn = (e: CustomEvent) => {
      const { emoji, x, y, label, targetName, sound } = e.detail;
      const burstId = `${Date.now()}-${Math.random()}`;
      
      setActiveBursts((prev) => [...prev, { id: burstId, emoji, x, y, label, targetName }]);
      setTimeout(() => {
        setActiveBursts((prev) => prev.filter((b) => b.id !== burstId));
      }, 2200);
    };

    window.addEventListener("spawn-emoji-burst", handleSpawn as EventListener);
    return () => window.removeEventListener("spawn-emoji-burst", handleSpawn as EventListener);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      <AnimatePresence>
        {activeBursts.map((burst) => (
          <motion.div
            key={burst.id}
            initial={{ scale: 0.2, opacity: 0, x: burst.x, y: burst.y }}
            animate={{
              scale: [0.2, 1.4, 1.1, 1],
              opacity: [0, 1, 1, 0],
              y: burst.y - 120,
              rotate: [0, -10, 10, -5, 0],
            }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 1.8, ease: "easeOut" }}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center select-none"
          >
            {/* Floating main reaction bubble */}
            <div className="relative flex items-center justify-center p-3 rounded-3xl bg-slate-900/90 border border-white/20 shadow-[0_10px_30px_rgba(0,0,0,0.8)] backdrop-blur-md">
              <span className="text-4xl md:text-5xl filter drop-shadow-lg animate-bounce">
                {burst.emoji}
              </span>
              {burst.targetName && (
                <span className="absolute -bottom-5 px-2 py-0.5 bg-cyan-950/90 border border-cyan-500/40 text-cyan-300 text-[10px] font-black rounded-full whitespace-nowrap shadow-md">
                  {burst.targetName}
                </span>
              )}
            </div>

            {/* Sparkles / Mini flying particle duplicates */}
            {[...Array(8)].map((_, i) => {
              const angle = (i / 8) * Math.PI * 2;
              const distance = 40 + Math.random() * 35;
              const px = Math.cos(angle) * distance;
              const py = Math.sin(angle) * distance;
              return (
                <motion.span
                  key={i}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 0.8 }}
                  animate={{
                    x: px,
                    y: py - 20,
                    opacity: 0,
                    scale: 0.2,
                  }}
                  transition={{ duration: 1.2, delay: 0.1 + i * 0.05, ease: "easeOut" }}
                  className="absolute text-xl pointer-events-none select-none"
                >
                  {burst.emoji}
                </motion.span>
              );
            })}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
